<script>
import { useProductsStore } from "@/store/products";
import Breadcrumbs from "@/components/molecules/breadcrumbs/default";
import Button from "@/components/atoms/button";
import ButtonBacktop from "@/components/molecules/button-backtop";
import Card from "@/components/molecules/card";
import CardHeading from "@/components/molecules/card/card-heading";
import Container from "@/components/atoms/container";
import LazyImage from "@/components/atoms/lazy-image";
import LinkTo from "@/components/atoms/link-to";
import Titre from "@/components/atoms/titre";
import Carousel from "@/components/organisms/carousel";
import CarouselSlide from "@/components/organisms/carousel/carousel-slide";
import { mapState } from "pinia";
import { pushProps, usePush } from "@/components/pages/mixins/push-mixin";
import { useInteractionDoneTracking } from "@/mixins/tracking/interaction-done";
import { usePageMarquesScroll } from "@/components/pages/mixins/marques-scroll.mixin.js";
import MarquesPageCodes from "@/utils/page-marques-codes";

export default {
  components: {
    Breadcrumbs,
    Button,
    ButtonBacktop,
    Card,
    CardHeading,
    Container,
    LazyImage,
    LinkTo,
    Titre,
    Carousel,
    CarouselSlide
  },
  props: {
    ...pushProps()
  },
  setup(props) {
    const { trackEvent } = useInteractionDoneTracking();

    const { onIntersect } = usePageMarquesScroll();

    const { getPushData } = usePush(props.pushZones);

    return { trackEvent, onIntersect, getPushData };
  },
  data() {
    return {
      alphabet: [
        "A",
        "B",
        "C",
        "D",
        "E",
        "F",
        "G",
        "H",
        "I",
        "J",
        "K",
        "L",
        "M",
        "N",
        "O",
        "P",
        "Q",
        "R",
        "S",
        "T",
        "U",
        "V",
        "W",
        "X",
        "Y",
        "Z",
        "#"
      ],
      marquesPageCodes: MarquesPageCodes
    };
  },
  computed: {
    ...mapState(useProductsStore, [
      "title",
      "breadcrumbs",
      "childrenCategories"
    ]),
    breadcrumb() {
      return this.breadcrumbs.map((item) => ({
        text: item.title,
        route: item.slug
      }));
    },
    marquesByLetter() {
      const sortedMarques = {};
      this.childrenCategories.forEach((marque) => {
        const firstLetter = marque.attributes.title.slice(0, 1).toUpperCase();
        if (this.alphabet.includes(firstLetter)) {
          sortedMarques[firstLetter]
            ? sortedMarques[firstLetter].push(marque)
            : (sortedMarques[firstLetter] = [marque]);
        } else {
          sortedMarques["#"]
            ? sortedMarques["#"].push(marque)
            : (sortedMarques["#"] = [marque]);
        }
      });
      return sortedMarques;
    },
    listeMarquesCora() {
      return this.childrenCategories.filter(
        (marque) => marque.attributes.is_marque_cora
      );
    },
    listeMarquesUne() {
      const marquesUne = [];
      if (
        Object.keys(this.getPushData(MarquesPageCodes.MARQUE_ALAUNE)).length !==
        0
      ) {
        marquesUne.push(this.getPushData(MarquesPageCodes.MARQUE_ALAUNE));
      }
      if (
        Object.keys(this.getPushData(MarquesPageCodes.MARQUE_ALAUNE, 2))
          .length !== 0
      ) {
        marquesUne.push(this.getPushData(MarquesPageCodes.MARQUE_ALAUNE, 2));
      }
      return marquesUne;
    }
  },
  methods: {
    scrollTo(id) {
      const position = document.getElementById(id).getBoundingClientRect();
      window.scrollTo({
        top: window.scrollY + position.top - 75,
        behavior: "smooth"
      });
    },
    trackingJeDecouvre(marque) {
      this.trackEvent({
        categoryGA: trackingCodes.CATEGORYGA.listeMarque,
        actionGA: trackingCodes.ACTIONGA.jeDecouvre,
        labelGA: marque
      });
    }
  }
};
</script>
<template>
  <div class="u-bgcolor-white">
    <Container size="sm">
      <Breadcrumbs
        :breadcrumbs="breadcrumb"
        :divider="false"
        class="u-text-left"
      />
      <Titre
        tag="h1"
        variant="primary"
        size="xlg"
        class="u-pb-md"
      >
        {{ title }}
      </Titre>
      <div class="c-marque-listing__button-wrapper">
        <Button
          v-if="listeMarquesCora.length > 0"
          size="md"
          outline
          radius="md"
          @click="scrollTo('marquesCora')"
        >
          <span class="u-color-text">Les marques Cora</span>
        </Button>
        <Button
          size="md"
          outline
          radius="md"
          @click="scrollTo('toutesMarques')"
        >
          <span class="u-color-text">Toutes les marques du moment</span>
        </Button>
      </div>
      <Carousel
        v-if="Object.keys(marquesByLetter).length > 9"
        :elements-per-slide="'auto'"
        free-mode
        scroll-bar
        class="c-marque-listing__alphabet u-mt-sm u-mb-lg20"
      >
        <CarouselSlide
          v-for="letter in alphabet"
          :key="letter"
          class="c-marque-listing__letter-wrapper"
        >
          <Button
            v-if="marquesByLetter[letter]"
            no-underline
            class="c-marque-listing__letter"
            @click="scrollTo(letter)"
          >
            {{ letter }}
          </Button>
          <span
            v-else
            class="c-marque-listing__letter c-marque-listing__letter--disabled"
          >
            {{ letter }}
          </span>
        </CarouselSlide>
      </Carousel>
      <Titre
        v-if="listeMarquesUne.length > 0"
        tag="h2"
        size="lg"
        variant="primary"
        class="u-pb-md"
      >
        Les marques à la Une
      </Titre>
      <div class="c-marque-listing__a-la-une-cards u-mb-lg20 u-mb-lg@sm">
        <Card
          v-for="marqueUne in listeMarquesUne"
          :key="marqueUne.id"
          radius="md"
          elevation="sm"
          border-color="grey-5"
        >
          <div class="u-flex u-flex-column u-flex-grow u-full-height">
            <CardHeading
              :img-path="marqueUne.image"
              radius="sm"
              img-height="18rem"
            />
            <div class="u-flex u-flex-grow u-pr-md u-pb-md u-pl-md">
              <div class="c-marque-listing__logo u-mr-sm">
                <LazyImage :src="marqueUne.logo" />
              </div>
              <div class="u-flex u-flex-column u-flex-align-start">
                <div class="u-flex-grow">
                  <Titre
                    tag="h3"
                    size="xs"
                    variant="primary"
                    class="u-my-xs"
                  >
                    {{ marqueUne.titre_action }}
                  </Titre>
                  <p class="u-font-14">
                    {{ marqueUne.description }}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="primary"
                  radius="full"
                  class="u-mt-md"
                  :href="marqueUne.lien_cta.startsWith('http') ? marqueUne.lien_cta : null"
                  :to="marqueUne.lien_cta.startsWith('http') ? null : marqueUne.lien_cta"
                  :target="marqueUne.lien_cta.startsWith('http') ? '_blank' : null"
                  @click="trackingJeDecouvre(marqueUne.titre_action)"
                >
                  {{ marqueUne.libelle_cta }}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
      <template v-if="listeMarquesCora.length > 0">
        <Titre
          id="marquesCora"
          tag="h2"
          size="lg"
          variant="primary"
          class="u-pb-md"
        >
          Les marques Cora
        </Titre>
        <div
          id="marquesCora"
          v-intersect="onIntersect"
          class="c-marque-listing__marques-wrapper"
        >
          <Card
            v-for="marqueCora in listeMarquesCora"
            :key="marqueCora.id"
            radius="md"
            elevation="sm"
            border-color="grey-5"
            class="c-marque-listing__card u-p-sm"
          >
            <div class="u-flex u-flex-column u-flex-align-center u-full-height">
              <LazyImage :src="marqueCora.attributes.visuel" />
              <div class="u-pt-sm u-font-primary u-flex u-flex-column u-flex-grow u-full-width">
                <div class="u-font-14 u-flex-grow">
                  <p class="u-font-16 u-font-700 u-mb-xs">
                    {{ marqueCora.attributes.title }}
                  </p>
                  <p class="u-font-400 u-mb-xs">
                    {{ marqueCora.attributes.description_courte }}
                  </p>
                </div>
                <LinkTo
                  :href="marqueCora.attributes.slug"
                  variant="primary"
                  hover-color="primary-light"
                  class="u-font-14 u-font-400"
                >
                  En savoir plus
                </LinkTo>
              </div>
            </div>
          </Card>
        </div>
      </template>
      <Titre
        id="toutesMarques"
        tag="h2"
        size="lg"
        variant="primary"
        class="u-pb-md"
      >
        Toutes les marques du moment
      </Titre>
      <Carousel
        v-if="Object.keys(marquesByLetter).length > 9"
        :elements-per-slide="'auto'"
        free-mode
        scroll-bar
        class="c-marque-listing__alphabet u-none u-block@sm u-mt-sm u-mb-lg"
      >
        <CarouselSlide
          v-for="letter in alphabet"
          :key="letter"
          class="c-marque-listing__letter-wrapper"
        >
          <Button
            v-if="marquesByLetter[letter]"
            no-underline
            class="c-marque-listing__letter"
            @click="scrollTo(letter)"
          >
            {{ letter }}
          </Button>
          <span
            v-else
            class="c-marque-listing__letter c-marque-listing__letter--disabled"
          >
            {{ letter }}
          </span>
        </CarouselSlide>
      </Carousel>
    </Container>
    <div class="c-marque-listing__repertoire-wrapper">
      <div class="c-marque-listing__repertoire">
        <div class="c-marque-listing__alphabet c-marque-listing__alphabet--vertical u-block u-none@sm">
          <div
            v-if="Object.keys(marquesByLetter).length > 9"
            class="c-marque-listing__letters-wrapper"
          >
            <template
              v-for="letter in alphabet"
              :key="letter"
            >
              <Button
                v-if="marquesByLetter[letter]"
                class="c-marque-listing__letter"
                @click="scrollTo(letter)"
              >
                {{ letter }}
              </Button>
              <span
                v-else
                class="c-marque-listing__letter c-marque-listing__letter--disabled"
              >
                {{ letter }}
              </span>
            </template>
          </div>
        </div>
        <div class="u-flex-grow">
          <template
            v-for="letter in alphabet"
            :key="letter"
          >
            <div
              v-if="marquesByLetter[letter]"
              :id="letter"
              class="c-marque-listing__marques-wrapper"
            >
              <Card
                radius="md"
                class="u-full-width u-full-height"
              >
                <div class="c-marque-listing__card-letter u-bgcolor-bg u-py-sm u-px-sm">
                  {{ letter }}
                </div>
              </Card>
              <Card
                v-for="marque in marquesByLetter[letter]"
                :key="marque.id"
                radius="md"
                elevation="sm"
                border-color="grey-5"
                class="c-marque-listing__card u-p-sm"
              >
                <div class="u-flex u-flex-column u-flex-align-center u-full-height">
                  <LazyImage :src="marque.attributes.visuel" />
                  <div class="u-pt-sm u-font-primary u-flex u-flex-column u-flex-grow u-full-width">
                    <div class="u-font-14 u-flex-grow">
                      <p class="u-font-16 u-font-700 u-mb-xs">
                        {{ marque.attributes.title }}
                      </p>
                      <p class="u-font-400 u-mb-xs">
                        {{ marque.attributes.description_courte }}
                      </p>
                    </div>
                    <LinkTo
                      :href="marque.attributes.slug"
                      variant="primary"
                      hover-color="primary-light"
                      class="u-font-14 u-font-400"
                    >
                      En savoir plus
                    </LinkTo>
                  </div>
                </div>
              </Card>
            </div>
          </template>
        </div>
        <div
          id="lettreZ"
          v-intersect="onIntersect"
        />
      </div>
    </div>

    <ButtonBacktop outline />
  </div>
</template>
<style lang="scss" scoped>
@import "@/assets/styles/components/page-liste-marque";
</style>
